/* eslint-disable no-await-in-loop */

import { FeedbackError } from '@/components/Feedback';
import { formularyStorage } from '@/modules/shared/adapters/CloudStorage';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AttachmentPreview from './components/AttachmentPreview';
import UploadInput from './components/UploadInput';
import { AttachmentUploadProps, DropzoneState } from './interfaces';

export const AttachmentUpload: React.FC<AttachmentUploadProps> = (props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onChange, urls, setUrls, withPortal, maxFiles = 20 } = props;

  const [status, setStatus] = useState<DropzoneState>('EMPTY');
  const [progress, setProgress] = useState<number>();
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const { t } = useTranslation();
  const modalSize = useBreakpointValue({ base: 'full', lg: '2xl' });

  const setUploadProgress = useCallback((prog: number) => {
    setProgress(prog);
  }, []);

  const onUploadSuccessful = useCallback(
    (url: string, fileName: string) => {
      setStatus('UPLOADED');
      setUrls((prev) => {
        const newUrls = [...prev, url];
        // Pass the updated fileNames to the parent component
        const updatedFileNames = {
          ...fileNames,
          [url]: fileName,
        };
        onChange(newUrls, updatedFileNames);
        setFileNames(updatedFileNames);
        return newUrls;
      });
    },
    [onChange, setUrls, fileNames],
  );

  const onRemove = React.useCallback(
    (index: number) => {
      setUrls((prev) => {
        const urlToRemove = prev[index];
        const newUrls = prev
          .slice(0, index)
          .concat(prev.slice(index + 1, prev.length));

        // Remove filename from the map
        const updatedFileNames = { ...fileNames };
        delete updatedFileNames[urlToRemove];
        setFileNames(updatedFileNames);

        // Pass the updated fileNames to the parent component
        onChange(newUrls, updatedFileNames);
        return newUrls;
      });
    },
    [onChange, setUrls, fileNames],
  );

  const onDownload = React.useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        try {
          setStatus('UPLOADING');
          formularyStorage.uploadFile({
            file,
            setProgress: setUploadProgress,
            setFileDownloadUrl: (url) => onUploadSuccessful(url, file.name),
          });
        } catch (e) {
          if (e instanceof Error) {
            FeedbackError({
              mainText: t(`common.error`),
              subText: t(`error.${e.message}`),
            });
            setStatus('EMPTY');
          }
        }
      }
    },
    [t, setUploadProgress, onUploadSuccessful],
  );

  return (
    <>
      <Button colorScheme="brand" onClick={onOpen}>
        {t('common.attach_files')}
      </Button>

      <Modal size={modalSize} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('common.send_files')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <UploadInput
                isUploading={status === 'UPLOADING'}
                progress={progress}
                onDrop={onDrop}
                withPortal={withPortal}
                maxFiles={maxFiles}
              />
              {urls.length > 0 && (
                <>
                  <Text>{t('common.attached_files')}</Text>
                  <Wrap w="full" spacing={3}>
                    {urls.map((url, index) => (
                      <WrapItem key={url}>
                        <AttachmentPreview
                          url={url}
                          fileName={fileNames[url]}
                          onRemove={() => onRemove(index)}
                          onDownload={() => onDownload(url)}
                        />
                      </WrapItem>
                    ))}
                  </Wrap>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              {t('common.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
